import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeCanvas } from 'qrcode.react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import toast from 'react-hot-toast';

const { FiX, FiCopy, FiCheck, FiSmartphone, FiShare2 } = FiIcons;

const RoomShareModal = ({ isOpen, onClose, roomName, password }) => {
  const [copied, setCopied] = useState({ name: false, password: false });

  if (!isOpen) return null;

  const shareUrl = `${window.location.origin}${window.location.pathname}?room=${encodeURIComponent(roomName)}&pass=${encodeURIComponent(password)}`;

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied({ ...copied, [type]: true });
      toast.success(`${type === 'name' ? 'Room name' : 'Password'} copied!`);
      setTimeout(() => setCopied({ ...copied, [type]: false }), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        exit={{ opacity: 0 }} 
        onClick={onClose} 
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-md" 
      />
      <motion.div 
        initial={{ scale: 0.9, opacity: 0, y: 20 }} 
        animate={{ scale: 1, opacity: 1, y: 0 }} 
        exit={{ scale: 0.9, opacity: 0, y: 20 }} 
        className="relative z-10 w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100"
      >
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
              <SafeIcon icon={FiShare2} className="w-5 h-5" />
            </div>
            <h3 className="font-black text-gray-900">Share Room Access</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-xl transition-colors text-gray-400">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <div className="p-8">
          <div className="bg-gray-50 rounded-2xl p-6 mb-8 flex flex-col items-center border border-gray-100 shadow-inner">
            <div className="bg-white p-4 rounded-xl shadow-sm mb-4">
              <QRCodeCanvas 
                value={shareUrl} 
                size={180}
                level="H"
                includeMargin={false}
                imageSettings={{
                  src: "/vite.svg",
                  height: 24,
                  width: 24,
                  excavate: true,
                }}
              />
            </div>
            <div className="flex items-center gap-2 text-blue-600 font-bold text-sm">
              <SafeIcon icon={FiSmartphone} className="w-4 h-4" />
              Scan to join on mobile
            </div>
          </div>

          <div className="space-y-4">
            <CredentialRow 
              label="Room Name" 
              value={roomName} 
              onCopy={() => copyToClipboard(roomName, 'name')}
              isCopied={copied.name}
            />
            <CredentialRow 
              label="Password" 
              value={password} 
              onCopy={() => copyToClipboard(password, 'password')}
              isCopied={copied.password}
            />
          </div>

          <p className="mt-8 text-center text-xs text-gray-400 font-medium leading-relaxed uppercase tracking-tighter">
            Credentials expire in 60 minutes of inactivity
          </p>
        </div>
      </motion.div>
    </div>
  );
};

const CredentialRow = ({ label, value, onCopy, isCopied }) => (
  <div>
    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">{label}</label>
    <div className="flex items-center gap-2">
      <div className="flex-1 p-3.5 bg-gray-50 rounded-xl font-mono text-sm border border-gray-100 text-gray-900 shadow-inner overflow-hidden text-ellipsis whitespace-nowrap">
        {value}
      </div>
      <motion.button 
        onClick={onCopy}
        className={`p-3.5 rounded-xl transition-all shadow-sm ${isCopied ? 'bg-green-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-blue-600 hover:text-blue-600'}`}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <SafeIcon icon={isCopied ? FiCheck : FiCopy} className="w-4 h-4" />
      </motion.button>
    </div>
  </div>
);

export default RoomShareModal;